import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  CreateTransitAddressWithdrawDTO,
  useGetTransitAddressesWithdrawalAddressesQuery,
} from '../../../../api';

type CreateAddressModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onCancel: () => void;
  isLoading: boolean;
  fields: CreateTransitAddressWithdrawDTO['body'] | null;
  fieldsHandler: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  submitDisabling: boolean;
};

export const CreateWithdrawalModal: FC<CreateAddressModalProps> = (props) => {
  const { open, onClose, onSuccess, onCancel, isLoading, fields, fieldsHandler, submitDisabling } =
    props;
  const { t } = useTranslation();

  // api
  const { data: withdrawalAddresses, isLoading: isCoinsLoading } =
    useGetTransitAddressesWithdrawalAddressesQuery();

  // elements
  const coinsOptions = withdrawalAddresses?.addresses.map((address) => {
    return (
      <MenuItem key={address} value={address}>
        {address}
      </MenuItem>
    );
  });

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: '380px' } }}>
      <DialogTitle>{t('transit-addresses.add-withdraw-modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="address"
            value={fields?.address ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('transit-addresses.add-withdraw-modal.fields.address')}
            fullWidth
          >
            {coinsOptions}
          </TextField>

          <TextField
            name="amount"
            value={fields?.amount}
            onChange={(e) => fieldsHandler(e)}
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            label={t('transit-addresses.add-withdraw-modal.fields.amount')}
            fullWidth
          />

          <TextField
            name="password"
            value={fields?.password ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('transit-addresses.add-withdraw-modal.fields.password')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onSuccess()}
          disabled={submitDisabling || isLoading || isCoinsLoading}
        >
          {t('transit-addresses.add-withdraw-modal.send-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onCancel()}>
          {t('transit-addresses.add-withdraw-modal.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
